import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import styles from "./HowToUse.module.css";
import adminStyles from "../Manage/Admin/AdminShared.module.css";
import Footer from "../../Components/Footer/Footer";
import BarLoader from "../../UI/Loaders/BarLoader/BarLoader";
import { ErrorBoundary } from "../../HOC/ErrorHandling/ErrorBoundary/ErrorBoundary";

const HowToUse = () => {
  const loadingStatus = useSelector(
    (state) => state.loadingRequests.pendingLoadRequests,
  );
  const user = useSelector((state) => state.auth.user);
  /* eslint eqeqeq: 0 */
  const isAdmin = user && user.isAdmin == true;

  return (
    <div className={styles["page-root"]}>
      {loadingStatus > 0 && (
        <div className="bar-loader-container">
          <div className="bar-loader-wrap">
            <BarLoader />
          </div>
        </div>
      )}

      <header className={styles.topbar}>
        <div className={styles["topbar-left"]}>
          <div className={styles["brand-block"]}>
            <span className={styles["brand-kicker"]}>Reference</span>
            <span className={styles["brand-title"]}>
              Ignite <span className={styles["brand-accent"]}>Elysium</span>
            </span>
          </div>
        </div>
        <div className={styles["topbar-right"]}>
          <Link to="/" className={styles["topbar-link"]}>
            Public site
          </Link>
          <Link
            to="/manage"
            className={
              isAdmin ? styles["topbar-link-primary"] : styles["topbar-link"]
            }
          >
            {isAdmin ? "Open admin" : "Admin login"}
          </Link>
        </div>
      </header>

      <main className={styles.main}>
        <div className={adminStyles["page-header"]}>
          <div className={adminStyles["page-header-content"]}>
            <h1 className={adminStyles["page-title"]}>How to use Manage</h1>
            <p className={adminStyles["page-subtitle"]}>
              Quick reference for the administrator console — catalog, content,
              editors, and site structure.
            </p>
          </div>
          <div className={adminStyles["page-actions"]}>
            <Link to="/manage" className={adminStyles["btn-primary"]}>
              Go to admin
            </Link>
            <Link to="/manage/help" className={adminStyles["btn-ghost"]}>
              In-app help
            </Link>
          </div>
        </div>

        <div className={adminStyles.panel}>
          <div className={adminStyles["help-section"]}>
            <h3>Admin console</h3>
            <p>
              Day-to-day management lives at{" "}
              <Link to="/manage">
                <code>/manage</code>
              </Link>
              . Sign in with an administrator account to use:
            </p>
            <ul>
              <li>
                <strong>Dashboard</strong> — counts, type breakdown, recent
                edits
              </li>
              <li>
                <strong>Catalog</strong> — songs, albums, playlists, streaming
                services
              </li>
              <li>
                <strong>Content</strong> — notices, about, social links, menu
                order, join splash, join invite
              </li>
              <li>
                <strong>Metadata</strong> — aggregated services, tags, and field
                values
              </li>
              <li>
                <strong>Tools</strong> — refresh data, export JSON
              </li>
              <li>
                <strong>Help</strong> — the same notes, inside the admin shell
              </li>
            </ul>
          </div>

          <div className={adminStyles["help-section"]}>
            <h3>Rich text editors</h3>
            <ul>
              <li>
                Used for content fields such as <code>text</code>,{" "}
                <code>textTwo</code>, <code>textThree</code>, and{" "}
                <code>textFour</code>.
              </li>
              <li>
                The site uses a special process for gradient text, so standard
                text color often will not stick. Use the editor&apos;s{" "}
                <strong>Background Color</strong> control to set text color.
              </li>
              <li>The first colors in the picker are theme colors.</li>
            </ul>
          </div>

          <div className={adminStyles["help-section"]}>
            <h3>Menu order</h3>
            <ul>
              <li>
                Create or edit a content item with type <code>menuOrder</code>.
              </li>
              <li>
                Put the order in the <code>menuOrder</code> field as a
                comma-separated list.
              </li>
              <li>
                Example:{" "}
                <code>noticeOne, noticeThree, about, noticeTwo, music</code>
              </li>
            </ul>
          </div>

          <div className={adminStyles["help-section"]}>
            <h3>Catalog types</h3>
            <ul>
              <li>
                <code>song</code>, <code>album</code>, <code>playlist</code> —
                music items with streaming URLs per service.
              </li>
              <li>
                <code>service</code> — a streaming platform (slug, embed
                attributes). Active services become URL fields on music items.
              </li>
              <li>
                Flags such as <code>isDefaultPlaylist</code>,{" "}
                <code>isFeaturedPlaylist</code>, and{" "}
                <code>isDefaultService</code> control home-page defaults.
              </li>
            </ul>
          </div>

          <div className={adminStyles["help-section"]}>
            <h3>Content types</h3>
            <ul>
              <li>
                <code>about</code>, <code>noticeOne</code>,{" "}
                <code>noticeTwo</code>, <code>noticeThree</code> — public site
                sections.
              </li>
              <li>
                <code>socialConnection</code> — social profile links.
              </li>
              <li>
                <code>connectSplash</code> — Join / Kit confirmation screens.
                One active item drives both. Blank text fields keep the site
                defaults. Point Kit&apos;s confirmation thank-you URL to{" "}
                <code>/connected</code>.
              </li>
              <li>
                <code>joinInvite</code> — homepage email invite popup. Uncheck
                Show the invite modal on an active item to hide it. Header,
                page, and footer Join stay available.
              </li>
              <li>
                Set <code>active</code> to show or hide a section on the public
                site.
              </li>
            </ul>
          </div>

          <div className={adminStyles["help-section"]}>
            <h3>Draft, publish, preview</h3>
            <ul>
              <li>
                <strong>Save draft</strong> stores unpublished copy on the same
                item. Visitors still see the live snapshot.
              </li>
              <li>
                <strong>Publish</strong> writes the published snapshot. The item
                still has to be Active for visitors to see it. Publish does not
                turn Active on or off.
              </li>
              <li>
                <strong>Revert to published</strong> restores the last published
                copy in the form and clears the draft. Visitors are not
                affected.
              </li>
              <li>
                <strong>Preview</strong> opens the real site with unpublished
                copy and adds this item to a preview set. Visitors are
                unaffected.
              </li>
              <li>
                <strong>Include</strong> pins this item in the preview set
                without opening a tab, so you can preview several items
                together. Catalog and content lists mark pinned items with an
                In preview badge. A second Join Invite or Connect Splash
                replaces the previous one of that type.
              </li>
              <li>
                <code>/?joininvite=1</code> without <code>preview=1</code> still
                previews the published Join Invite.
              </li>
              <li>
                Phone: log in as admin on that origin, Save draft, then open
                Preview. Session cookie is about 43 minutes; JWT is 1 day.
                Overlay is not a credential.
              </li>
            </ul>
          </div>

          <div className={adminStyles["help-section"]}>
            <h3>Keyboard (admin editor)</h3>
            <ul>
              <li>
                <code>⌘/Ctrl + S</code> — Save draft in the item editor
              </li>
              <li>
                <code>Esc</code> — dismiss confirm dialogs
              </li>
            </ul>
          </div>
        </div>
      </main>

      <div className={styles.footer}>
        <ErrorBoundary>
          <Footer />
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default HowToUse;
